"use client";

import { useEffect, useRef, useCallback } from "react";
import { useRouter } from "next/navigation";
import { Header, Footer } from "@/components/common";
import { HomePage } from "@/components/custom/home";

// The interactive half of the homepage. It lives here rather than in page.jsx
// so that page.jsx can stay a server component and export its own metadata —
// a client page silently loses `metadata`, which left `/` inheriting the root
// layout's 83-character title and 207-character description.
const HomeShell = () => {
  const router = useRouter();

  // Scroll to hash element (e.g. #pricing, #contact) on initial page load
  const hasScrolledToHash = useRef(false);
  useEffect(() => {
    if (hasScrolledToHash.current) return;
    const hash = window.location.hash.replace("#", "");
    if (!hash) return;

    // Poll for the element since it may not be in the DOM yet (animations, lazy rendering)
    const interval = setInterval(() => {
      const el = document.getElementById(hash);
      if (el) {
        clearInterval(interval);
        hasScrolledToHash.current = true;
        el.scrollIntoView({ behavior: "smooth" });
        // Clean hash from URL to prevent re-scrolling on navigation
        history.replaceState(null, "", window.location.pathname + window.location.search);
      }
    }, 100);

    // Stop polling after 5s as a safety net
    const timeout = setTimeout(() => clearInterval(interval), 5000);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, []);

  // Kept as the setActiveTab prop contract used by Header/HomePage children;
  // now navigates to the real product routes.
  const changeTab = useCallback(
    (tab) => {
      router.push(tab === "homepage" ? "/" : `/${tab}`);
    },
    [router],
  );

  return (
    <div className="min-h-screen bg-[#212121] relative">
      <div className="relative z-10">
        <Header setActiveTab={changeTab} />
        <main id="main">
          <HomePage setActiveTab={changeTab} />
        </main>
        <Footer />
      </div>
    </div>
  );
};

export default HomeShell;
